import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { ApiClient } from '../services/apiClient';
import StatCardGrid, { StatCardItem } from '../components/StatCardGrid';
import SimUsageGraph, { UsageBucket } from '../components/SimUsageGraph';
import { Row, Col, Select, Input, Spin, Alert, Card, Table, Typography } from 'antd';
import {
  FieldTimeOutlined,
  PhoneOutlined,
  MessageOutlined,
  CloudDownloadOutlined,
  MobileOutlined,
  WifiOutlined,
} from '@ant-design/icons';
import PageHelp from '../components/PageHelp';
import PageHeader from '../components/PageHeader';
import EditableFriendlyName from '../components/EditableFriendlyName';
import { useDisplaySettings } from '../theme/DisplaySettingsContext';
import { blurStyle } from '../utils/redact';
import { getMySims, MySim } from '../services/adminService';
import {
  Period,
  generatePeriodOptions,
  periodToDateRange,
  periodToGroupBy,
} from '../utils/dateRange';
import { parseNumber, formatKilobytes, formatDuration } from '../utils/formatUsage';

const { Option } = Select;
const { Text } = Typography;

type GiacomPhone = { phoneNumber: string; giacomUsername?: string };

type GiacomSimSummary = {
  phoneNumber: string;
  giacomUsername: string;
  call_count: number;
  sms_count: number;
  data_count: number;
  total_data_volume: number;
  total_call_duration: number;
};

type UsageByRangeResponse = {
  buckets: UsageBucket[];
};

type UsageSummaryResponse = {
  sims: GiacomSimSummary[];
};

const PERIOD_OPTIONS = generatePeriodOptions();

function buildPhoneParam(
  searchText: string,
  phones: GiacomPhone[],
  friendlyByPhone: Record<string, { assignmentId: number; friendlyName: string | null }>
): string {
  if (!searchText) return '';
  const q = searchText.toLowerCase();
  const matched = phones
    .filter(
      p =>
        p.phoneNumber.toLowerCase().includes(q) ||
        (p.giacomUsername || '').toLowerCase().includes(q) ||
        (friendlyByPhone[p.phoneNumber]?.friendlyName || '').toLowerCase().includes(q)
    )
    .map(p => p.phoneNumber);
  return matched.length > 0 ? `&phoneNumbers=${matched.join(',')}` : '';
}

function CrewSIMs() {
  const auth = useAuth();

  const [simSummary, setSimSummary] = useState<GiacomSimSummary[]>([]);
  const { redactNames } = useDisplaySettings();
  const [buckets, setBuckets] = useState<UsageBucket[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<GiacomPhone[]>([]);
  const [searchText, setSearchText] = useState('');
  const [period, setPeriod] = useState<Period>('thisMonth');
  const [loading, setLoading] = useState(false);
  const [graphLoading, setGraphLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mySims, setMySims] = useState<MySim[]>([]);
  const [simsLoading, setSimsLoading] = useState(false);
  const [printMode, setPrintMode] = useState(false);

  const isFirstRender = useRef(true);
  const graphAbortRef = useRef<AbortController | null>(null);
  const graphDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Main effect: fires on period/auth change.
  // Phase 1 fetches summary + phone list in parallel so tiles and table update quickly.
  // Phase 2 fetches the graph with the current search filter applied to the fresh phone data.
  useEffect(() => {
    if (!auth.authenticated || !auth.token || auth.isLoading) return;

    const controller = new AbortController();
    const { signal } = controller;

    const load = async () => {
      setLoading(true);
      setGraphLoading(true);
      setError(null);
      try {
        const { startDate, endDate } = periodToDateRange(period);
        const groupBy = periodToGroupBy(period);

        // Phase 1: summary + phone list
        const [summaryData, phoneData] = await Promise.all([
          ApiClient.get<UsageSummaryResponse>(
            `/giacom/usage/summary?startDate=${startDate}&endDate=${endDate}`,
            signal
          ),
          ApiClient.get<{ phoneNumbers: GiacomPhone[] }>('/giacom/phone-numbers', signal),
        ]);

        const sims = Array.isArray(summaryData.sims) ? summaryData.sims : [];
        const phones = phoneData?.phoneNumbers?.filter(p => !!p.phoneNumber) ?? [];
        setSimSummary(sims);
        setPhoneNumbers(phones);
        setLoading(false);

        // Phase 2: graph with current search filter applied to the fresh phone list.
        // searchText intentionally read from closure — search effect handles its own updates.
        const phoneParam = buildPhoneParam(searchText, phones, friendlyByPhone);
        const usageData = await ApiClient.get<UsageByRangeResponse>(
          `/giacom/usage-by-range?startDate=${startDate}&endDate=${endDate}&period=${groupBy}${phoneParam}`,
          signal
        );
        setBuckets(Array.isArray(usageData.buckets) ? usageData.buckets : []);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('CrewSIMs: Error fetching data:', err);
        setError('Failed to load usage data. Please try again.');
        setSimSummary([]);
        setBuckets([]);
      } finally {
        setLoading(false);
        setGraphLoading(false);
      }
    };

    void load();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.authenticated, auth.token, auth.isLoading, period]);

  // Debounced graph re-fetch when search text changes.
  // Skipped on initial mount (main effect handles the first graph load).
  // AbortController cancels any in-flight request when the user keeps typing.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!auth.authenticated || !auth.token) return;

    if (graphDebounceRef.current) clearTimeout(graphDebounceRef.current);

    graphDebounceRef.current = setTimeout(async () => {
      if (graphAbortRef.current) graphAbortRef.current.abort();
      graphAbortRef.current = new AbortController();
      const { signal } = graphAbortRef.current;

      setGraphLoading(true);
      try {
        const { startDate, endDate } = periodToDateRange(period);
        const groupBy = periodToGroupBy(period);
        const phoneParam = buildPhoneParam(searchText, phoneNumbers, friendlyByPhone);

        const usageData = await ApiClient.get<UsageByRangeResponse>(
          `/giacom/usage-by-range?startDate=${startDate}&endDate=${endDate}&period=${groupBy}${phoneParam}`,
          signal
        );
        setBuckets(Array.isArray(usageData.buckets) ? usageData.buckets : []);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('CrewSIMs: Graph re-fetch error:', err);
      } finally {
        setGraphLoading(false);
      }
    }, 300);

    return () => {
      if (graphDebounceRef.current) clearTimeout(graphDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // mySims fetch — separate, no period dependency
  useEffect(() => {
    if (!auth.authenticated || !auth.token || auth.isLoading) return;
    setSimsLoading(true);
    getMySims()
      .then(sims => {
        setMySims(
          sims.filter(
            s => s.provider === 'giacom' || s.sim_type === 'crew' || s.sim_type === 'personal'
          )
        );
      })
      .catch(err => console.error('CrewSIMs: Error fetching my-sims:', err))
      .finally(() => setSimsLoading(false));
  }, [auth.authenticated, auth.token, auth.isLoading]);

  const friendlyByPhone = useMemo(() => {
    const map: Record<
      string,
      { assignmentId: number; friendlyName: string | null; assetName: string | null }
    > = {};
    for (const sim of mySims) {
      const entry = {
        assignmentId: sim.assignment_id,
        friendlyName: sim.friendly_name,
        assetName: sim.asset_name,
      };
      const id = sim.resource_identifier;
      map[id] = entry;
      if (id.startsWith('0')) {
        map[id.slice(1)] = entry;
      } else if (/^\d+$/.test(id)) {
        map['0' + id] = entry;
      }
    }
    return map;
  }, [mySims]);

  const displaySims = useMemo(() => {
    const summaryByPhone = new Map(simSummary.map(s => [s.phoneNumber, s]));
    const pool = phoneNumbers.map(phone => {
      const summary = summaryByPhone.get(phone.phoneNumber);
      return (
        summary ?? {
          phoneNumber: phone.phoneNumber,
          giacomUsername: phone.giacomUsername ?? '',
          call_count: 0,
          sms_count: 0,
          data_count: 0,
          total_data_volume: 0,
          total_call_duration: 0,
        }
      );
    });

    if (!searchText) return pool;
    const q = searchText.toLowerCase();
    return pool.filter(
      s =>
        s.phoneNumber.toLowerCase().includes(q) ||
        (s.giacomUsername || '').toLowerCase().includes(q) ||
        (friendlyByPhone[s.phoneNumber]?.friendlyName || '').toLowerCase().includes(q)
    );
  }, [simSummary, phoneNumbers, searchText, friendlyByPhone]);

  // Stat tiles derive from displaySims so they reflect search.
  const statCards: StatCardItem[] = useMemo(() => {
    const totals = displaySims.reduce(
      (acc, sim) => ({
        call_count: acc.call_count + sim.call_count,
        sms_count: acc.sms_count + sim.sms_count,
        total_data_volume: acc.total_data_volume + sim.total_data_volume,
        total_call_duration: acc.total_call_duration + sim.total_call_duration,
      }),
      { call_count: 0, sms_count: 0, total_data_volume: 0, total_call_duration: 0 }
    );

    const dataVolume = formatKilobytes(totals.total_data_volume);
    const duration = formatDuration(totals.total_call_duration, { maxUnits: 2 });

    const simsWithUsage = displaySims.filter(
      s => s.call_count > 0 || s.sms_count > 0 || s.total_data_volume > 0
    ).length;

    return [
      { title: 'Total SIMs', value: phoneNumbers.length, icon: <MobileOutlined /> },
      { title: 'SIMs with Usage', value: simsWithUsage, icon: <WifiOutlined /> },
      { title: 'Call Duration', value: duration.formatted, icon: <FieldTimeOutlined /> },
      { title: 'Calls', value: parseNumber(totals.call_count), icon: <PhoneOutlined /> },
      { title: 'SMS', value: parseNumber(totals.sms_count), icon: <MessageOutlined /> },
      {
        title: 'Data',
        value: dataVolume.value,
        icon: <CloudDownloadOutlined />,
        suffix: dataVolume.unit,
      },
    ];
  }, [displaySims, phoneNumbers]);

  const currentPeriodLabel = PERIOD_OPTIONS.find(p => p.value === period)?.label || String(period);

  const tableColumns = [
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      sorter: (a: GiacomSimSummary, b: GiacomSimSummary) =>
        a.phoneNumber.localeCompare(b.phoneNumber),
    },
    {
      title: 'Username',
      dataIndex: 'giacomUsername',
      key: 'giacomUsername',
      sorter: (a: GiacomSimSummary, b: GiacomSimSummary) =>
        (a.giacomUsername || '').localeCompare(b.giacomUsername || ''),
      render: (name: string) => <span style={blurStyle(redactNames)}>{name || '—'}</span>,
    },
    {
      title: 'Friendly Name',
      key: 'friendlyName',
      width: 220,
      sorter: (a: GiacomSimSummary, b: GiacomSimSummary) =>
        (friendlyByPhone[a.phoneNumber]?.friendlyName || '').localeCompare(
          friendlyByPhone[b.phoneNumber]?.friendlyName || ''
        ),
      render: (_: any, record: GiacomSimSummary) => {
        const entry = friendlyByPhone[record.phoneNumber];
        return (
          <EditableFriendlyName
            assignmentId={entry?.assignmentId}
            value={entry?.friendlyName}
            canEdit={!!entry}
            blurred={redactNames}
            onSaved={next =>
              entry &&
              setMySims(prev =>
                prev.map(s =>
                  s.resource_identifier === record.phoneNumber ? { ...s, friendly_name: next } : s
                )
              )
            }
          />
        );
      },
    },
    {
      title: 'Vessel',
      key: 'vessel',
      sorter: (a: GiacomSimSummary, b: GiacomSimSummary) =>
        (friendlyByPhone[a.phoneNumber]?.assetName || '').localeCompare(
          friendlyByPhone[b.phoneNumber]?.assetName || ''
        ),
      render: (_: any, record: GiacomSimSummary) => {
        const assetName = friendlyByPhone[record.phoneNumber]?.assetName;
        return (
          <Text type={assetName ? undefined : 'secondary'} style={blurStyle(redactNames)}>
            {assetName || '—'}
          </Text>
        );
      },
    },
    {
      title: 'Calls',
      dataIndex: 'call_count',
      key: 'call_count',
      sorter: (a: GiacomSimSummary, b: GiacomSimSummary) => a.call_count - b.call_count,
      render: (v: number) => <Text>{v}</Text>,
    },
    {
      title: 'SMS',
      dataIndex: 'sms_count',
      key: 'sms_count',
      sorter: (a: GiacomSimSummary, b: GiacomSimSummary) => a.sms_count - b.sms_count,
      render: (v: number) => <Text>{v}</Text>,
    },
    {
      title: 'Data',
      key: 'data',
      sorter: (a: GiacomSimSummary, b: GiacomSimSummary) =>
        a.total_data_volume - b.total_data_volume,
      defaultSortOrder: 'descend' as const,
      render: (_: any, record: GiacomSimSummary) => {
        const fmt = formatKilobytes(record.total_data_volume);
        return (
          <Text strong>
            {fmt.value} {fmt.unit}
          </Text>
        );
      },
    },
    {
      title: 'Call Duration',
      key: 'duration',
      sorter: (a: GiacomSimSummary, b: GiacomSimSummary) =>
        a.total_call_duration - b.total_call_duration,
      render: (_: any, record: GiacomSimSummary) => {
        const fmt = formatDuration(record.total_call_duration, { maxUnits: 2 });
        return <Text>{fmt.formatted}</Text>;
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Crew SIMs"
        exportConfig={{
          filename: () => {
            const periodLabel =
              PERIOD_OPTIONS.find(p => p.value === period)?.label || String(period);
            return `MIMO-CrewSIMs-${periodLabel.replace(/\s+/g, '-')}`;
          },
          disabled: loading || !simSummary.length,
          onBeforePrint: () => {
            setPrintMode(true);
            window.dispatchEvent(new Event('resize'));
          },
          onAfterPrint: () => setPrintMode(false),
          prePrintDelayMs: 500,
        }}
        actions={
          <PageHelp
            title="Crew SIMs Help"
            sections={[
              {
                title: 'Overview',
                content:
                  'This page shows usage for crew mobile phone SIMs (calls, SMS, data). Use the period selector to change the time range and the search box to find specific numbers, usernames, or friendly names.',
              },
              {
                title: 'Stat Cards',
                content:
                  'Calls: total number of calls\nSMS: total text messages\nData: total data usage\nCall Duration: total time on calls',
              },
              {
                title: 'Charts',
                content:
                  'Bar charts show daily breakdown of calls, SMS, and data. The time grouping adjusts based on the selected period (daily for month, monthly for year).',
              },
            ]}
          />
        }
      />
      <div className="print-only">
        <h2 className="print-page-title">MIMO Connect — Crew SIMs Usage Report</h2>
        <p className="print-meta">Generated {new Date().toLocaleString()}</p>
        <div className="print-filter-summary">
          <strong>Period:</strong>{' '}
          {PERIOD_OPTIONS.find(p => p.value === period)?.label || String(period)}
          {searchText && (
            <>
              {' | '}
              <strong>Search:</strong> &ldquo;{searchText}&rdquo;
            </>
          )}
        </div>
      </div>
      <Row
        gutter={[16, 16]}
        style={{ marginBottom: 16, alignItems: 'center' }}
        className="no-print"
      >
        <Col xs={24} sm={24} md={12}>
          <Select<Period> value={period} onChange={v => setPeriod(v)} style={{ width: '100%' }}>
            {PERIOD_OPTIONS.map(({ label, value }) => (
              <Option key={value} value={value}>
                {label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Input.Search
            placeholder="Search phone number, ICCID, username, or friendly name..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
          />
        </Col>
      </Row>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      <Spin spinning={loading}>
        <StatCardGrid stats={statCards} lgColumns={3} />
      </Spin>
      <Card title="SIM Usage" style={{ marginTop: 16 }}>
        <Table<GiacomSimSummary>
          rowKey="phoneNumber"
          dataSource={displaySims}
          loading={loading || simsLoading}
          columns={tableColumns}
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: searchText ? 'No SIMs match your search' : 'No usage data for this period',
          }}
          pagination={
            printMode
              ? false
              : {
                  defaultPageSize: 20,
                  pageSizeOptions: [10, 20, 50, 100],
                  showSizeChanger: true,
                  showTotal: total => `${total} SIM${total === 1 ? '' : 's'}`,
                }
          }
        />
      </Card>
      <Card title={`Usage Over Time — ${currentPeriodLabel}`} style={{ marginTop: 16 }}>
        <Spin spinning={graphLoading}>
          <SimUsageGraph buckets={buckets} period={period} />
        </Spin>
      </Card>
    </>
  );
}

export default CrewSIMs;
